import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { LiveEventBus, moodForHour } from "./mockNetwork";
import { CAMERA_LATLNG } from "./RoomCanvas";
import { useKaizenLiveOffers, offerRideId } from "../../hooks/useKaizenLiveOffers";
import { useLiveTrips, acceptanceState, haloColorForStatus } from "../../hooks/useLiveTrips";
import { useCallSession } from "../../hooks/useCallSession";
import { useLiveDrivers } from "../../hooks/useLiveDrivers";
import { useNovaLiveEvents } from "../../hooks/useNovaLiveEvents";
import { apiClient } from "../../lib/api";

/**
 * RoomProvider — the room's nervous system.
 *
 * Holds:
 *   - the mock network (mutated over time)
 *   - the day-arc mood
 *   - camera as {center: [lat,lng], zoom} — Leaflet map obeys this
 *   - focus target (what the workspace is looking at)
 *   - operator identity + coworker name + preferences
 *   - cinematic moments queue
 *
 * Default camera opens on Houston (active theater), NOT on empty world —
 * the dispatcher enters an operation already in progress.
 */

const RoomContext = createContext(null);
export const useRoom = () => useContext(RoomContext);

const PREF_KEY = "astat.floor.identity";
const FOLLOW_NOVA_KEY = "astat.floor.followNova";
const SPOTLIGHT_IDLE_MS_DEFAULT = 9000;

export function RoomProvider({ children }) {
  const [network] = useState(() => new LiveEventBus());
  const [now, setNow] = useState(() => new Date());
  const [tickN, setTickN] = useState(0);
  const [camera, setCamera] = useState(CAMERA_LATLNG.world);
  const [cameraStack, setCameraStack] = useState([]);      // history for esc-pop
  const [focus, setFocus] = useState(null);
  const [pinnedTrips, setPinnedTrips] = useState([]);      // VA multi-trip hold
  const [sosTripId, setSosTripId] = useState(null);        // one at a time, dominant
  const [cinematic, setCinematic] = useState(null);
  const [showCommand, setShowCommand] = useState(false);
  const [dialerOpen, setDialerOpen] = useState(false);
  const [identity, setIdentity] = useState(() => {
    try { const raw = localStorage.getItem(PREF_KEY); return raw ? JSON.parse(raw) : null; }
    catch (_e) { return null; }
  });

  // ─── REAL DATA — Kaizen offers (shared hook, same path as KaizenPanel) ─
  // The Floor's peripheral ticker + Coworker whispers are populated ONLY
  // from real signals wired in this file (offers, trip status, driver GPS/
  // shift, assignments) — every VA decision flows through the shared hook,
  // the exact same code path KaizenPanel uses. Zero duplicate logic.
  const onOfferArrival = useCallback((o) => {
    const off = o.offer || {};
    const rid = offerRideId(o);
    if (!rid) return;
    const pickup = off.pickup_address || "pickup unknown";
    const drop = off.dropoff_address || "dropoff unknown";
    const mob = off.mobility_type || "trip";
    try {
      network.addWhisper(
        `Kaizen offer ${rid} · ${mob} · ${pickup.split(",")[0]} → ${drop.split(",")[0]} · pre-verifying`,
        0.92, true,
      );
      network.addEvent("offer", `Kaizen offer ${rid} arrived`, "calm");
    } catch { /* noop */ }
  }, [network]);
  const kaizen = useKaizenLiveOffers({ pollMs: 5000, onOfferArrival });

  // ─── REAL DATA — Live trips (shared hook, same path as TPTripsPanel) ───
  const onTripStateChange = useCallback((trip, prev) => {
    const rid = trip.broker_ride_id || trip.trip_id?.slice(0, 8) || "trip";
    try {
      network.addWhisper(
        `${rid} · ${prev} → ${trip.status}`,
        0.88,
        trip.status === "OFFER_EXHAUSTED" || trip.status === "NO_SHOW_CONFIRMED",
      );
      network.addEvent("trip", `Trip ${rid} · ${trip.status}`, "calm");
    } catch { /* noop */ }
  }, [network]);
  const tripsData = useLiveTrips({ pollMs: 30000, onStateChange: onTripStateChange });

  // ─── VOIP breakout room session (shared with all Floor components) ───
  const callSession = useCallSession({ pollMs: 4000 });

  // ─── REAL DATA — live GPS drivers (single shared poll — RoomCanvas's map
  // pucks and FocusWorkspace's driver detail card read the exact same list,
  // so a click on a real puck always resolves to a real driver record). ───
  const { drivers: liveDrivers, allDrivers, refetch: refetchLiveDrivers } = useLiveDrivers({ pollMs: 8000 });

  // ─── Real peripheral ticker events — driver movement (GPS delta between
  // polls) and shift online/offline transitions. First poll never fires an
  // event (nothing to compare against yet), so there's no burst on load.
  const prevDriverPosRef = useRef({});
  useEffect(() => {
    const prev = prevDriverPosRef.current;
    const next = {};
    (liveDrivers || []).forEach((d) => {
      next[d.driver_id] = { lat: d.last_seen_lat, lng: d.last_seen_lng };
      const p = prev[d.driver_id];
      if (p && p.lat != null && d.last_seen_lat != null) {
        const movedDeg = Math.abs(d.last_seen_lat - p.lat) + Math.abs(d.last_seen_lng - p.lng);
        if (movedDeg > 0.0004) { // ~35-45m since the last poll — a real GPS move, not jitter
          try { network.addEvent("driver_move", `${d.name} is moving`, "calm"); } catch { /* noop */ }
        }
      }
    });
    prevDriverPosRef.current = next;
  }, [liveDrivers, network]);

  const prevShiftRef = useRef({});
  useEffect(() => {
    const prev = prevShiftRef.current;
    const next = {};
    (allDrivers || []).forEach((d) => {
      const status = String(d.shift_status || "").toLowerCase();
      next[d.driver_id] = status;
      const was = prev[d.driver_id];
      if (was !== undefined && was !== status) {
        try {
          if (status === "offline" && was !== "offline") network.addEvent("driver_shift", `${d.name} went offline`, "calm");
          else if (was === "offline" && status !== "offline") network.addEvent("driver_shift", `${d.name} is online`, "calm");
        } catch { /* noop */ }
      }
    });
    prevShiftRef.current = next;
  }, [allDrivers, network]);

  // ─── REAL DATA — transportation providers (TPs). Same list ProviderChips
  // derives its counts from and TP Settings/CRUD uses — the canonical
  // `GET /tpt/providers`. Polled slowly; contract/roster/billing terms don't
  // change second-to-second.
  const [realProviders, setRealProviders] = useState([]);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await apiClient.get("/tpt/providers");
        if (alive) setRealProviders(Array.isArray(data) ? data : []);
      } catch { /* best-effort */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // ─── Nova Learning Log — capture starts now ─────────────────────
  // Fire-and-forget. Never blocks or breaks the calling action if it fails.
  const logNovaEvent = useCallback(async (evt) => {
    try { await apiClient.post("/floor/nova/learning-event", evt); }
    catch { /* best-effort — never breaks the calling action */ }
  }, []);

  // Trip-level assignment through the single canonical endpoint.
  const assignTrip = useCallback(async (tripId, payload) => {
    const res = await tripsData.assign(tripId, payload);
    if (res.ok) {
      try {
        network.addWhisper(
          `assignment sent for ${String(tripId).slice(0, 8)} · driver has 90s to accept`,
          0.94, false,
        );
        network.addEvent("assign", `Trip ${String(tripId).slice(0, 8)} assigned`, "calm");
      } catch { /* noop */ }
      logNovaEvent({
        trip_id: tripId,
        gate: "assign",
        decision_source: payload?.mode === "auto_assigner" ? "auto_assign"
                        : payload?.mode === "broadcast" ? "broadcast" : "manual",
        meta: { tp_id: payload?.tp_id, driver_id: payload?.driver_id },
      });
    } else {
      try {
        network.addWhisper(
          `assignment failed for ${String(tripId).slice(0, 8)} · ${res.error || "?"}`,
          0.8, true,
        );
      } catch { /* noop */ }
    }
    return res;
  }, [tripsData, network, logNovaEvent]);

  // Broadcast — real endpoint, real schema (scope: network/tp/tps/custom),
  // separate from assign(). Was previously mis-wired through assign() with a
  // {mode:"broadcast"} payload assign_driver never actually understood.
  const broadcastTripAction = useCallback(async (tripId, payload) => {
    const res = await tripsData.broadcastTrip(tripId, payload);
    if (res.ok) {
      try {
        network.addWhisper(
          `broadcast sent for ${String(tripId).slice(0, 8)} · ${res.count ?? "network"} drivers notified`,
          0.9, false,
        );
      } catch { /* noop */ }
      logNovaEvent({ trip_id: tripId, gate: "assign", decision_source: "broadcast", meta: payload });
    } else {
      try {
        network.addWhisper(`broadcast failed for ${String(tripId).slice(0, 8)} · ${res.error || "?"}`, 0.8, true);
      } catch { /* noop */ }
    }
    return res;
  }, [tripsData, network, logNovaEvent]);

  // Fan a trip out to N TPs — the marketplace gate Live Ops calls "Offer".
  const offerToTps = useCallback(async (tripId, opts) => {
    const res = await tripsData.offerToTps(tripId, opts);
    if (res.ok) {
      try {
        network.addWhisper(
          `offered ${String(tripId).slice(0, 8)} to ${opts?.tp_ids?.length || 0} TP(s) · first-accept wins`,
          0.9, false,
        );
      } catch { /* noop */ }
      logNovaEvent({ trip_id: tripId, gate: "tp_offer", decision_source: "manual",
                      meta: { tp_count: opts?.tp_ids?.length || 0 } });
    } else {
      try { network.addWhisper(`couldn't offer ${String(tripId).slice(0, 8)} · ${res.error || "?"}`, 0.8, true); }
      catch { /* noop */ }
    }
    return res;
  }, [tripsData, network, logNovaEvent]);

  // ─── Real driver actions — SMS, shift toggle, ad-hoc trip creation,
  // provider SMS, operational broadcast. All narrate to Nova on success/
  // failure the same way trip actions do, so the whisper feed stays the
  // single source of "what just happened in this room."
  const smsDriver = useCallback(async (driverId, message, driverName) => {
    try {
      await apiClient.post(`/floor/drivers/${driverId}/sms`, { message });
      network.addWhisper(`texted ${driverName || "driver"} · "${message.slice(0, 40)}${message.length > 40 ? "…" : ""}"`, 0.95, false);
      return { ok: true };
    } catch (e) {
      const err = e?.response?.data?.detail || String(e);
      network.addWhisper(`couldn't text ${driverName || "driver"} · ${err}`, 0.8, true);
      return { ok: false, error: err };
    }
  }, [network]);

  const setDriverShift = useCallback(async (driverId, shiftStatus, driverName) => {
    try {
      await apiClient.patch(`/tpt/drivers/${driverId}/shift`, { shift_status: shiftStatus });
      network.addWhisper(`${driverName || "driver"} → ${shiftStatus}`, 0.9, false);
      refetchLiveDrivers();
      return { ok: true };
    } catch (e) {
      const err = e?.response?.data?.detail || String(e);
      network.addWhisper(`couldn't update ${driverName || "driver"}'s shift · ${err}`, 0.8, true);
      return { ok: false, error: err };
    }
  }, [network, refetchLiveDrivers]);

  const smsProvider = useCallback(async (tpId, message, tpName) => {
    try {
      await apiClient.post(`/floor/providers/${tpId}/sms`, { message });
      network.addWhisper(`texted ${tpName || "provider"} dispatch · "${message.slice(0, 40)}${message.length > 40 ? "…" : ""}"`, 0.95, false);
      return { ok: true };
    } catch (e) {
      const err = e?.response?.data?.detail || String(e);
      network.addWhisper(`couldn't text ${tpName || "provider"} · ${err}`, 0.8, true);
      return { ok: false, error: err };
    }
  }, [network]);

  const sendBroadcast = useCallback(async (scope, message, onlineOnly = true) => {
    try {
      const { data } = await apiClient.post("/floor/broadcast", { scope, message, online_only: onlineOnly });
      network.addWhisper(
        data.sent > 0
          ? `broadcast sent to ${data.sent}/${data.total} driver(s) · ${scope}`
          : `broadcast reached no one · ${data.note || "no matching drivers"}`,
        0.9, data.sent === 0,
      );
      return data;
    } catch (e) {
      const err = e?.response?.data?.detail || String(e);
      network.addWhisper(`broadcast failed · ${err}`, 0.8, true);
      return { ok: false, error: err };
    }
  }, [network]);

  const createTrip = useCallback(async (payload) => {
    try {
      const { data } = await apiClient.post("/tpt/trips", payload);
      network.addWhisper(`new trip created for ${payload.rider?.name || "rider"} · finding a driver`, 0.95, false);
      logNovaEvent({ trip_id: data?.trip_id, gate: "compose", decision_source: "manual", meta: {} });
      return { ok: true, trip: data };
    } catch (e) {
      const err = e?.response?.data?.detail || String(e);
      network.addWhisper(`couldn't create that trip · ${err}`, 0.8, true);
      return { ok: false, error: err };
    }
  }, [network, logNovaEvent]);


  useEffect(() => {
    const id = setInterval(() => {
      setNow(new Date());
      setTickN(n => n + 1);
    }, 900);
    return () => clearInterval(id);
  }, []);

  // ─── VA actions on offers (delegate to shared hook + narrate to Nova) ─
  const acceptOffer = useCallback(async (rideId, opts = {}) => {
    const res = await kaizen.accept(rideId, opts);
    if (res?.ok) {
      try {
        network.addWhisper(
          `you accepted ${rideId} · booking ${res.booking_id || "created"} · adding to the board`,
          0.98, false,
        );
      } catch { /* noop */ }
      setCinematic({ kind: "assignment", duration: 1400 });
      setTimeout(() => setCinematic(null), 1400);
    } else {
      try {
        network.addWhisper(
          `couldn't accept ${rideId} on kaizen · ${res?.error || "unknown"} · try again?`,
          0.82, true,
        );
      } catch { /* noop */ }
    }
    return res;
  }, [kaizen, network]);

  const declineOfferAction = useCallback(async (rideId) => {
    const res = await kaizen.decline(rideId);
    if (res?.ok) {
      try { network.addWhisper(`you declined ${rideId} · returned to broker`, 0.88, false); }
      catch { /* noop */ }
    }
    return res;
  }, [kaizen, network]);

  // ─── save identity to localStorage + server ─────────────────────
  const saveIdentity = useCallback(async (draft) => {
    setIdentity(draft);
    try { localStorage.setItem(PREF_KEY, JSON.stringify(draft)); } catch (_e) { /* noop */ }
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      if (backend && draft?.dispatcher_name) {
        await fetch(`${backend}/api/floor/identity`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(draft),
        });
      }
    } catch (_e) { /* noop */ }
  }, []);

  // ─── camera helpers ─────────────────────────────────────────────
  const cameraTo = useCallback((preset) => {
    if (typeof preset === "string" && CAMERA_LATLNG[preset]) {
      setCamera(CAMERA_LATLNG[preset]);
    } else if (preset && typeof preset === "object") {
      setCamera(preset);
    }
  }, []);

  // ─── focus workspace (with camera stack for esc-pop) ────────────
  // `meta` carries small hand-off payloads between components — e.g.
  // "Book pickup" on a hospital card prefilling the compose form.
  const focusOn = useCallback((kind, id, meta = null) => {
    setCameraStack(stack => [...stack, camera].slice(-6));  // remember where we were
    setFocus({ kind, id, meta });
  }, [camera]);

  const releaseFocus = useCallback(() => {
    setFocus(null);
    // Pop back to the camera we came from — the room remembers.
    setCameraStack(stack => {
      if (stack.length === 0) return stack;
      const prev = stack[stack.length - 1];
      setCamera(prev);
      return stack.slice(0, -1);
    });
  }, []);

  // Dev/QA hook — lets tests trigger focus without wrestling with the Leaflet
  // marker's shifting screen coordinates. Removed entirely by tree-shaking in
  // production if `window` guard fails; harmless in browser.
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__floorFocusOn = focusOn;
      window.__floorReleaseFocus = releaseFocus;
    }
  }, [focusOn, releaseFocus]);

  // ─── pinned trips (multi-focus tray) ────────────────────────────
  const pinTrip = useCallback((tripId) => {
    setPinnedTrips(p => (p.includes(tripId) ? p : [...p, tripId].slice(-8)));
  }, []);
  const unpinTrip = useCallback((tripId) => {
    setPinnedTrips(p => p.filter(x => x !== tripId));
  }, []);

  // ─── SOS state — REAL trips only ────────────────────────────────
  // A rider's panic tap (POST /api/rider/sos) sets trip.sos_flag on the
  // real trip document. We watch the same trips feed everything else
  // reads and auto-elevate the moment one appears — no manual trigger,
  // no mock trip. Dismissed only via `acknowledgeSos()`.
  const sosAcknowledgedRef = useRef(new Set());
  useEffect(() => {
    const live = tripsData.trips || [];
    const flagged = live.find(t => t.sos_flag && !sosAcknowledgedRef.current.has(t.trip_id));
    if (flagged && sosTripId !== flagged.trip_id) {
      setSosTripId(flagged.trip_id);
      setFocus({ kind: "realTrip", id: flagged.trip_id });
      setCinematic({ kind: "sos", duration: 2400 });
      setTimeout(() => setCinematic(null), 2400);
    } else if (!flagged && sosTripId) {
      setSosTripId(null);
    }
  }, [tripsData.trips, sosTripId]);
  const acknowledgeSos = useCallback(() => {
    if (sosTripId) sosAcknowledgedRef.current.add(sosTripId);
    setSosTripId(null);
  }, [sosTripId]);

  // ─── Real Nova narration — floor infra loops, not a mock array ──
  // Polls the two agent loops that already exist on the backend
  // (dead-man's-switch heartbeats + Kaizen orphan reconciliation) and
  // narrates anything genuinely new. This is what "Nova already
  // handled it" or "Nova is watching X" means — real background agents.
  const seenOrphansRef = useRef(new Set());
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await apiClient.get("/floor/orphans");
        (data || []).forEach(o => {
          if (!alive || seenOrphansRef.current.has(o.id)) return;
          seenOrphansRef.current.add(o.id);
          network.addWhisper(
            `orphan detected · ride ${o.ride_id} accepted on Kaizen but missing locally · reconciliation loop flagged it`,
            0.9, true,
          );
        });
      } catch { /* best-effort */ }
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [network]);

  // ─── Nova's trip-chaining engine — proposals + drift alerts ─────
  // Polls chain_routes.py; Nova only ever proposes/flags, a human still
  // has to tap approve/resolve before anything reaches a driver.
  const [chainProposals, setChainProposals] = useState([]);
  const [chainAlerts, setChainAlerts] = useState([]);
  const seenChainProposalsRef = useRef(new Set());
  const seenChainAlertsRef = useRef(new Set());
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const [pRes, aRes] = await Promise.all([
          apiClient.get("/tpt/chains/proposed"),
          apiClient.get("/tpt/chains/alerts"),
        ]);
        if (!alive) return;
        const proposals = pRes.data?.chains || [];
        const alerts = aRes.data?.alerts || [];
        setChainProposals(proposals);
        setChainAlerts(alerts);
        proposals.forEach(c => {
          if (seenChainProposalsRef.current.has(c.chain_id)) return;
          seenChainProposalsRef.current.add(c.chain_id);
          const first = c.legs?.[0];
          network.addWhisper(
            `nova proposed a ${c.legs?.length || 0}-stop route · ${first?.rider_name || "rider"} · ${c.chain_type === "same_client_multistop" ? "same client, multi-stop" : "multi-client bundle"} · needs approval`,
            0.85, true,
          );
        });
        alerts.forEach(a => {
          if (seenChainAlertsRef.current.has(a.alert_id)) return;
          seenChainAlertsRef.current.add(a.alert_id);
          network.addWhisper(
            `${a.rider_name || "next stop"} at risk · driver running ~${a.projected_delay_min}m behind · nova found options`,
            0.95, true,
          );
        });
      } catch { /* best-effort */ }
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [network]);

  const approveChain = useCallback(async (chainId, driverId, tpId, forceUncleared = false) => {
    const { data } = await apiClient.post(`/tpt/chains/${chainId}/approve`, {
      driver_id: driverId, tp_id: tpId, force_uncleared: forceUncleared,
    });
    setChainProposals(prev => prev.filter(c => c.chain_id !== chainId));
    network.addWhisper(`route approved · ${data.legs} stops assigned to one driver`, 0.9, true);
    return data;
  }, [network]);

  const rejectChain = useCallback(async (chainId) => {
    await apiClient.post(`/tpt/chains/${chainId}/reject`);
    setChainProposals(prev => prev.filter(c => c.chain_id !== chainId));
  }, []);

  const resolveChainAlert = useCallback(async (alertId, action, driverId, tpId, forceUncleared = false) => {
    const { data } = await apiClient.post(`/tpt/chains/alerts/${alertId}/resolve`, {
      action, driver_id: driverId, tp_id: tpId, force_uncleared: forceUncleared,
    });
    setChainAlerts(prev => prev.filter(a => a.alert_id !== alertId));
    network.addWhisper(action === "reassign" ? "reassigned the at-risk leg to a nearby driver" : "alert dismissed", 0.85, true);
    return data;
  }, [network]);

  // ─── Nova's general single-trip pickup-risk watch (core/trip_risk.py) ──
  // Same authority model as chain alerts — Nova proposes, VA/TP resolves.
  // Covers every trip type (Kaizen, CPD, standalone) that isn't part of an
  // active chain (chain_drift already watches those).
  const [tripRiskAlerts, setTripRiskAlerts] = useState([]);
  const seenTripRiskAlertsRef = useRef(new Set());
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await apiClient.get("/trip-risk/alerts");
        if (!alive) return;
        const alerts = data?.alerts || [];
        setTripRiskAlerts(alerts);
        alerts.forEach(a => {
          if (seenTripRiskAlertsRef.current.has(a.alert_id)) return;
          seenTripRiskAlertsRef.current.add(a.alert_id);
          const msg = a.kind === "cascading_failure"
            ? `${a.rider_name || "trip"} at risk · ${a.cascading_note || "driver declined"}${a.trip_type === "cpd" ? " · CPD" : ""}`
            : `${a.rider_name || "trip"} at risk · ${a.current_driver?.name || "driver"} running ~${a.projected_delay_min}m behind pickup${a.trip_type === "cpd" ? " · CPD" : ""}`;
          network.addWhisper(msg, 0.9, true);
        });
      } catch { /* best-effort */ }
    };
    poll();
    const id = setInterval(poll, 20000);
    return () => { alive = false; clearInterval(id); };
  }, [network]);

  const resolveTripRiskAlert = useCallback(async (alertId, action, driverId, tpId, forceUncleared = false) => {
    const { data } = await apiClient.post(`/trip-risk/alerts/${alertId}/resolve`, {
      action, driver_id: driverId, tp_id: tpId, force_uncleared: forceUncleared,
    });
    setTripRiskAlerts(prev => prev.filter(a => a.alert_id !== alertId));
    network.addWhisper(action === "reassign" ? "reassigned the at-risk trip to a nearby driver" : "risk alert dismissed", 0.85, true);
    return data;
  }, [network]);

  // ─── Instant archive — a VA shouldn't need to type into Nova chat to clear
  // a stale/test trip. Archiving always closes any open risk/chain alert for
  // that trip in the same backend call, so the banner can never outlive it.
  const archiveTrip = useCallback(async (tripId) => {
    const { data } = await apiClient.post(`/floor/trips/${tripId}/archive`);
    setTripRiskAlerts(prev => prev.filter(a => a.trip_id !== tripId));
    setChainAlerts(prev => prev.filter(a => a.at_risk_trip_id !== tripId));
    network.addWhisper(`trip archived${data.cleared_alerts ? ` · cleared ${data.cleared_alerts} alert(s)` : ""}`, 0.85, true);
    return data;
  }, [network]);

  // ─── Permanent delete — archives first if needed (server enforces this
  // as a safety net), then hard-deletes. Used by the Trip Room's own
  // Archive/Delete buttons so clearing test clutter never requires leaving
  // the trip you're looking at.
  const hardDeleteTrip = useCallback(async (tripId) => {
    try {
      await apiClient.delete(`/floor/trips/${tripId}`);
    } catch (e) {
      if (e?.response?.status === 409) {
        await apiClient.post(`/floor/trips/${tripId}/archive`);
        await apiClient.delete(`/floor/trips/${tripId}`);
      } else {
        throw e;
      }
    }
    setTripRiskAlerts(prev => prev.filter(a => a.trip_id !== tripId));
    setChainAlerts(prev => prev.filter(a => a.at_risk_trip_id !== tripId));
    network.addWhisper("trip permanently deleted", 0.85, true);
  }, [network]);

  // ─── Kaizen silent-failure watchdog ─────────────────────────────
  // Surfaces any pipeline step (email loop, warm-browser fast path, portal
  // scrape) that failed without a VA noticing — this must reach the Floor
  // itself, not just an admin sub-panel nobody's looking at.
  const [kaizenPipelineAlert, setKaizenPipelineAlert] = useState(null);
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await apiClient.get("/kaizen/pipeline-health");
        if (!alive) return;
        setKaizenPipelineAlert(data?.has_active_failure ? data : null);
      } catch { /* best-effort */ }
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // ─── Zone pressure watchdog ──────────────────────────────────────
  // Surfaces any operating zone that's short on supply vs today's trip
  // volume — same "reach the Floor, don't bury it in an admin panel"
  // principle as the Kaizen watchdog above. Dismissible in the banner
  // itself (see SystemBannerStack) since this isn't a hard failure.
  const [zonePressureAlert, setZonePressureAlert] = useState(null);
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await apiClient.get("/zones/stats/coverage?range=today");
        if (!alive) return;
        const short = (data?.zones || []).filter(z => z.tier === "short");
        setZonePressureAlert(short.length ? { zones: short } : null);
      } catch { /* best-effort */ }
    };
    poll();
    const id = setInterval(poll, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // ─── Active Nova · real trip-watch check ────────────────────────
  // Replaces the old fabricated "scan" status with an honest, verifiable
  // check: a trip is "running late for pickup" if the clock has passed its
  // already-scheduled pickup time (schedule.requested_pickup — set upstream
  // at booking time, already padded for travel) and no driver has arrived
  // at the pickup point yet. No invented confidence score — this is a plain
  // true/false report of what was actually checked.
  const WATCH_STATUSES = ["REQUESTED", "TP_ACCEPTED", "ASSIGNED", "ACCEPTED", "EN_ROUTE", "ARRIVED_PU", "ON_TRIP", "ARRIVED_DO"];
  const ARRIVED_STATUSES = ["ARRIVED_PU", "ON_TRIP", "ARRIVED_DO"];
  const STALE_MS = 48 * 60 * 60 * 1000;
  const scanTickRef = useRef(0);
  useEffect(() => {
    scanTickRef.current += 1;
    if (scanTickRef.current % 4 !== 0) return;
    const now = Date.now();
    const watching = (tripsData.trips || []).filter(t => {
      if (!WATCH_STATUSES.includes(String(t.status).toUpperCase())) return false;
      const ts = t.updated_at || t.created_at;
      const when = ts ? new Date(ts).getTime() : NaN;
      return Number.isFinite(when) && (now - when) < STALE_MS;
    });
    const lateCount = watching.filter(t => {
      if (ARRIVED_STATUSES.includes(String(t.status).toUpperCase())) return false;
      const pu = t.schedule?.requested_pickup;
      if (!pu) return false;
      const puWhen = new Date(pu).getTime();
      return Number.isFinite(puWhen) && now > puWhen;
    }).length;
    const scanText = lateCount > 0
      ? `nova scan · checked ${watching.length} active trip(s) · ${lateCount} running late for pickup`
      : `nova scan · checked ${watching.length} active trip(s) · all on time`;
    // Dedup guard — only drop this if it's actually new information, so
    // "all on time" every ~80s doesn't pile up into a wall of near-identical
    // cards (confusing, not reassuring) when nothing has changed.
    if (network.whispers[0]?.text === scanText) return;
    network.addWhisper(scanText, null, false, lateCount > 0 ? "warn" : null);
  }, [tripsData.trips, network]);

  // ─── Trust Ladder (Phase 2) — VA-approval-vs-Nova-suggestion score per
  // action category. Purely a visible score; Phase 3 gated autonomy below
  // is what actually acts on it, and only once an admin explicitly opts a
  // category in (see autonomyModes/setAutonomyMode).
  const [trustLadder, setTrustLadder] = useState([]);
  const refetchTrustLadder = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/floor/nova/trust-ladder");
      setTrustLadder(data?.ladder || []);
    } catch { /* best-effort */ }
  }, []);
  useEffect(() => {
    refetchTrustLadder();
    const id = setInterval(refetchTrustLadder, 60000);
    return () => clearInterval(id);
  }, [refetchTrustLadder]);

  // ─── Phase 3 · Gated autonomy "training wheels" — Manual → Training
  // Wheels (Nova auto-acts + a room-wide Undo banner) → Autonomous. Default
  // mode is "manual" everywhere until an admin flips it (see
  // core/nova_autonomy.py). autoResolvedAlerts drives the Undo banner.
  const [autonomyModes, setAutonomyModes] = useState({});
  const [autoResolvedAlerts, setAutoResolvedAlerts] = useState([]);
  const seenAutoResolvedRef = useRef(new Set());
  const refetchAutonomyModes = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/trip-risk/autonomy");
      setAutonomyModes(data?.modes || {});
    } catch { /* best-effort */ }
  }, []);
  const setAutonomyMode = useCallback(async (actionType, mode) => {
    await apiClient.put(`/trip-risk/autonomy/${actionType}`, { mode });
    await refetchAutonomyModes();
  }, [refetchAutonomyModes]);
  const undoAutoAlert = useCallback(async (alertId) => {
    await apiClient.post(`/trip-risk/alerts/${alertId}/undo-auto`);
    setAutoResolvedAlerts(prev => prev.filter(a => a.alert_id !== alertId));
    network.addWhisper("undid Nova's auto-reassignment — back to the previous driver", 0.85, true);
  }, [network]);
  useEffect(() => {
    refetchAutonomyModes();
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await apiClient.get("/trip-risk/alerts/auto-recent");
        if (!alive) return;
        const alerts = data?.alerts || [];
        setAutoResolvedAlerts(alerts);
        alerts.forEach(a => {
          if (seenAutoResolvedRef.current.has(a.alert_id)) return;
          seenAutoResolvedRef.current.add(a.alert_id);
          network.addWhisper(
            `nova auto-reassigned ${a.rider_name || "a trip"} to ${a.suggested_driver_name || "a nearby driver"} — training wheels, undo available`,
            0.9, true,
          );
        });
      } catch { /* best-effort */ }
    };
    poll();
    const id = setInterval(poll, 15000);
    return () => { alive = false; clearInterval(id); };
  }, [refetchAutonomyModes, network]);

  // ─── Live Autonomy Mode — "Follow Nova" (one unified real-event stream) ──
  // Merges the Kaizen intake cascade + trip-risk reassignment autonomy into
  // one feed (core/nova_live.py). Off by default — a VA who never flips this
  // on is entirely unaffected; their screen never moves on its own. When on,
  // every genuinely new real event opens/highlights the matching trip room,
  // and settles back to wherever the VA was once Nova goes quiet for a bit.
  const [followNova, setFollowNovaState] = useState(() => {
    try { return localStorage.getItem(FOLLOW_NOVA_KEY) === "1"; } catch (_e) { return false; }
  });
  const setFollowNova = useCallback((val) => {
    setFollowNovaState(val);
    try { localStorage.setItem(FOLLOW_NOVA_KEY, val ? "1" : "0"); } catch (_e) { /* noop */ }
  }, []);
  const { events: novaLiveEvents, latestEvent: novaLatestEvent } = useNovaLiveEvents({ pollMs: 3000 });
  const [spotlightEvent, setSpotlightEvent] = useState(null);
  const [spotlightIdleMs, setSpotlightIdleMsState] = useState(SPOTLIGHT_IDLE_MS_DEFAULT);
  const refetchSpotlightSettings = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/floor/nova/spotlight-settings");
      if (data?.idle_ms) setSpotlightIdleMsState(data.idle_ms);
    } catch { /* best-effort, keeps default */ }
  }, []);
  useEffect(() => { refetchSpotlightSettings(); }, [refetchSpotlightSettings]);
  const setSpotlightIdleMs = useCallback(async (ms) => {
    await apiClient.put("/floor/nova/spotlight-settings", { idle_ms: ms });
    await refetchSpotlightSettings();
  }, [refetchSpotlightSettings]);
  const preFollowStateRef = useRef(null);   // {focus, camera} captured the moment Follow Nova turned on
  const spotlightIdleTimerRef = useRef(null);
  const prevFollowRef = useRef(false);

  const settleBackFromSpotlight = useCallback(() => {
    const pre = preFollowStateRef.current;
    preFollowStateRef.current = null;
    setSpotlightEvent(null);
    if (pre) {
      setFocus(pre.focus);
      setCamera(pre.camera);
    }
  }, []);

  const dismissSpotlight = useCallback(() => {
    if (spotlightIdleTimerRef.current) clearTimeout(spotlightIdleTimerRef.current);
    setSpotlightEvent(null);
  }, []);

  // Capture "where the VA was" exactly once, the moment Follow Nova is
  // switched on — and restore it immediately if switched back off.
  useEffect(() => {
    if (followNova && !prevFollowRef.current) {
      preFollowStateRef.current = { focus, camera };
    }
    if (!followNova && prevFollowRef.current) {
      if (spotlightIdleTimerRef.current) clearTimeout(spotlightIdleTimerRef.current);
      settleBackFromSpotlight();
    }
    prevFollowRef.current = followNova;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followNova]);

  // The actual "walk from card to card" — a genuinely new real event opens
  // the matching trip room and resets the quiet-period clock.
  useEffect(() => {
    if (!followNova || !novaLatestEvent) return;
    if (spotlightIdleTimerRef.current) clearTimeout(spotlightIdleTimerRef.current);
    setSpotlightEvent(novaLatestEvent);
    if (novaLatestEvent.trip_id) {
      setFocus({ kind: "realTrip", id: novaLatestEvent.trip_id });
    }
    spotlightIdleTimerRef.current = setTimeout(settleBackFromSpotlight, spotlightIdleMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [novaLatestEvent, followNova]);

  useEffect(() => () => { if (spotlightIdleTimerRef.current) clearTimeout(spotlightIdleTimerRef.current); }, []);

  // Step back in time — reopen any past step from the list. Works whether
  // or not Follow Nova is currently on; doesn't disturb the idle clock of a
  // live spotlight session if one happens to be running.
  const replayEvent = useCallback((evt) => {
    if (!evt) return;
    if (spotlightIdleTimerRef.current) clearTimeout(spotlightIdleTimerRef.current);
    setSpotlightEvent(evt);
    if (evt.trip_id) {
      setCameraStack(stack => [...stack, camera].slice(-6));
      setFocus({ kind: "realTrip", id: evt.trip_id });
    }
    if (followNova) {
      spotlightIdleTimerRef.current = setTimeout(settleBackFromSpotlight, spotlightIdleMs);
    }
  }, [camera, followNova, settleBackFromSpotlight, spotlightIdleMs]);

  // ─── cinematic moments ─────────────────────────────────────────
  const playCinematic = useCallback((moment) => {
    setCinematic(moment);
    const dur = moment.duration || 1200;
    setTimeout(() => setCinematic(null), dur);
  }, []);

  // ─── keyboard: `/` opens command, ESC releases, `P` pins trip ────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "/" && !showCommand && document.activeElement?.tagName !== "INPUT") {
        e.preventDefault();
        setShowCommand(true);
      } else if (e.key === "Escape") {
        if (showCommand) setShowCommand(false);
        else if (focus) releaseFocus();
      } else if (e.key === "1" && !showCommand) {
        cameraTo("world");
      } else if (e.key === "2" && !showCommand) {
        cameraTo("chicago");
      } else if (e.key === "3" && !showCommand) {
        cameraTo("houston");
      } else if ((e.key === "p" || e.key === "P") && !showCommand && focus?.kind === "realTrip") {
        if (pinnedTrips.includes(focus.id)) unpinTrip(focus.id);
        else pinTrip(focus.id);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [showCommand, focus, releaseFocus, cameraTo, pinnedTrips, pinTrip, unpinTrip]);

  const mood = useMemo(() => moodForHour(now.getHours()), [now]);

  const value = useMemo(() => ({
    network, now, tickN, mood,
    camera, cameraTo,
    focus, focusOn, releaseFocus,
    pinnedTrips, pinTrip, unpinTrip,
    sosTripId, acknowledgeSos,
    cinematic, playCinematic,
    showCommand, setShowCommand,
    dialerOpen, setDialerOpen,
    identity, saveIdentity,
    // Shared Kaizen path (same hook KaizenPanel uses)
    incomingOffers: kaizen.offers,
    offerFetchAt: kaizen.fetchedAt,
    offerBusy: kaizen.busy,
    offerSuggestions: kaizen.suggestions,
    loadOfferSuggestions: kaizen.loadSuggestions,
    acceptOffer,
    declineOffer: declineOfferAction,
    // Shared Trips path (same hook TPTripsPanel uses)
    liveTrips: tripsData.liveTrips,
    allTrips: tripsData.trips,
    tripsConnected: tripsData.connected,
    findTrip: tripsData.findTrip,
    assignTrip,
    broadcastTrip: broadcastTripAction,
    offerToTps,
    logNovaEvent,
    acceptanceState,
    haloColorForStatus,
    // VOIP breakout room (shared session)
    callSession,
    // Real (non-mock) network data — shared across map, chips, workspace
    liveDrivers, refetchLiveDrivers, realProviders, allDrivers,
    smsDriver, setDriverShift, smsProvider, sendBroadcast, createTrip,
    // Nova trip-chaining
    chainProposals, chainAlerts, approveChain, rejectChain, resolveChainAlert,
    // Nova single-trip pickup-risk watch
    tripRiskAlerts, resolveTripRiskAlert, archiveTrip,
    // Instant permanent delete (Trip Room / Manage Trips buttons)
    hardDeleteTrip,
    trustLadder, refetchTrustLadder,
    // Nova Phase 3 — gated autonomy training wheels
    autonomyModes, setAutonomyMode, autoResolvedAlerts, undoAutoAlert,
    // Kaizen silent-failure watchdog
    kaizenPipelineAlert,
    // Zone pressure watchdog
    zonePressureAlert,
    // Live Autonomy Mode — "Follow Nova"
    followNova, setFollowNova, novaLiveEvents, spotlightEvent, replayEvent, dismissSpotlight,
    spotlightIdleMs, setSpotlightIdleMs,
  }), [network, now, tickN, mood, camera, cameraTo,
       focus, focusOn, releaseFocus,
       pinnedTrips, pinTrip, unpinTrip,
       sosTripId, acknowledgeSos,
       cinematic, playCinematic,
       showCommand, identity, saveIdentity, dialerOpen,
       kaizen.offers, kaizen.fetchedAt, kaizen.busy, kaizen.suggestions, kaizen.loadSuggestions,
       acceptOffer, declineOfferAction,
       tripsData.liveTrips, tripsData.trips, tripsData.connected, tripsData.findTrip,
       assignTrip, broadcastTripAction, offerToTps, logNovaEvent, callSession,
       liveDrivers, refetchLiveDrivers, realProviders, allDrivers,
       smsDriver, setDriverShift, smsProvider, sendBroadcast, createTrip,
       chainProposals, chainAlerts, approveChain, rejectChain, resolveChainAlert,
       tripRiskAlerts, resolveTripRiskAlert, archiveTrip, hardDeleteTrip, trustLadder, refetchTrustLadder,
       autonomyModes, setAutonomyMode, autoResolvedAlerts, undoAutoAlert,
       kaizenPipelineAlert, zonePressureAlert,
       followNova, setFollowNova, novaLiveEvents, spotlightEvent, replayEvent, dismissSpotlight,
       spotlightIdleMs, setSpotlightIdleMs]);

  return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>;
}
